import css from './DrilldownMenu.component.scss';
import html from './DrilldownMenu.component.html';

/**
 * A menu enabling the user to drill down into a report.
 * 
 * The menu is two levels deep and provides a top level of categories,
 * each with its own sub-menu of selectable options.
 * It is absolutely positioned at an offset specified when opened.
 * 
 * Events:
 * * `menuclose` - Fired when the menu closes.
 * * `optionselect` - Fired when the user selects an option.
 * The event detail contains the selected value.
 */
export class DrilldownMenu extends HTMLElement {
    /**
     * Initializes the element.
     */
    constructor() {
        super();

        const style = document.createElement('style');
        const template = document.createElement('template');

        style.textContent = css;

        template.innerHTML = html;

        this.attachShadow({ mode: 'open' });

        /**
         * The timer for a pending close of the menu.
         * 
         * @private
         */
        this.closing = undefined;

        const { shadowRoot } = this;

        shadowRoot.append(style, template.content.cloneNode(true));

        this.addEventListener('mouseenter', () => this.handleMouseEnter());
        this.addEventListener('mouseleave', () => this.handleMouseLeave());
        this.addEventListener('transitionend', () => this.handleTransitionEnd());
    }

    /**
     * Sets the options available in the menu.
     * 
     * @param {Array<{category: string, options: string[]}>} options The options to set.
     * 
     * @public
     */
    set options(options) {
        const categories = options.map(category => this.createCategory(category));

        const root = this.shadowRoot.getElementById('menu');

        root.innerHTML = '';

        root.append(...categories);
    }

    /**
     * Closes the menu.
     * 
     * @public
     */
    close() {
        this.cancelOpen();

        this.closeCategory();

        this.dispatchEvent(new CustomEvent('menuclose'));

        this.style.width = '';
    }

    /**
     * Opens the menu at the specified CSS left and top.
     * 
     * @param {number} left The CSS left offset for the menu.
     * @param {number} top The CSS top offset for the menu.
     * 
     * @public
     */
    open(left, top) {
        this.cancelClose();

        this.classList.add('opening');

        this.size(left, top);
    }

    /**
     * Cancels any pending close of the menu.
     * 
     * @private
     */
    cancelClose() {
        clearTimeout(this.closing);
    }

    /**
     * Cancels any pending open of the menu.
     * 
     * @private
     */
    cancelOpen() {
        const { classList } = this;

        classList.remove('open');
        classList.remove('opening');
    }

    /**
     * Closes any open category in the menu.
     * 
     * @private
     */
    closeCategory() {
        this.shadowRoot.querySelector('.open')?.classList.remove('open');
    }

    /**
     * Creates an element for the specified category.
     * 
     * @param {{category: string, options: string[]}} category The category to create.
     * 
     * @returns {HTMLElement}
     * 
     * @private
     */
    createCategory({ category, options }) {
        const button = document.createElement('button');
        const container = document.createElement('div');
        const icon = document.createElement('span');
        const menu = document.createElement('div');

        const optionElements = options.map(option => this.createOption(option));

        const text = document.createElement('span');

        button.addEventListener('click', event => this.handleCategoryClick(event));
        button.addEventListener('mouseenter', event => this.handleCategoryMouseEnter(event));

        button.append(text, icon);

        container.classList.add('category');

        container.append(button, menu);

        icon.classList.add('icon');

        icon.textContent = '\ue080';

        menu.append(...optionElements);

        menu.classList.add('menu');

        text.classList.add('text');

        text.textContent = category;

        return container;
    }

    /**
     * Creates an element for the specified option.
     * 
     * @param {string} option The option to create.
     * 
     * @private
     */
    createOption(option) {
        const element = document.createElement('button');

        element.addEventListener('click', () => this.handleOptionClick(option));

        element.classList.add('option');

        element.textContent = option;

        return element;
    }

    /**
     * Runs when the user clicks a category.
     * 
     * Opens the category.
     * 
     * @param {Event} event The click event.
     * 
     * @private
     */
    handleCategoryClick(event) {
        this.openCategory(event.currentTarget.parentElement);
    }

    /**
     * Runs when the user's mouse enters a category.
     * 
     * Opens the category.
     * 
     * @param {Event} event The mouseover event.
     * 
     * @private
     */
    handleCategoryMouseEnter(event) {
        this.openCategory(event.target.parentElement);
    }

    /**
     * Runs when the user's mouse enters the menu.
     * 
     * Cancels any pending close of the menu.
     * 
     * @private
     */
    handleMouseEnter() {
        this.cancelClose();
    }

    /**
     * Runs when the user clicks an option.
     * 
     * Dispatches the `optionselect` event with the clicked value.
     * 
     * @param {string} option The clicked option value.
     * 
     * @private
     */
    handleOptionClick(option) {
        this.sendOptionSelect(option);
    }

    /**
     * Runs when the user's mouse leaves the menu.
     * 
     * Sets a timer to close the menu.
     * 
     * @private
     */
    handleMouseLeave() {
        this.closing = setTimeout(() => this.close(), 500);
    }

    /**
     * Runs when a transition ends in the menu.
     * 
     * If the transition occurred because the menu was opening,
     * puts the menu into its final open state.
     * 
     * @private
     */
    handleTransitionEnd() {
        if (this.classList.contains('opening')) {
            this.setOpen();
        }
    }

    /**
     * Opens the specified category.
     * 
     * First closes any open category.
     * If the category menu would vertically overflow its containing block,
     * offsets it just enough to fit.
     * 
     * @param {HTMLElement} category The category to open.
     * 
     * @private
     */
    openCategory(category) {
        const boxShadowWidth = 2;

        const menu = category.querySelector('.menu');

        this.closeCategory();

        category.classList.add('open');

        const overflow =
            this.offsetTop
            + category.offsetTop
            + menu.offsetHeight
            + boxShadowWidth
            - this.offsetParent.offsetHeight;

        if (overflow > 0) {
            menu.style.top = `${-overflow}px`;
        }
    }

    /**
     * Dispatches the `optionselect` event with the specified value.
     * 
     * @param {string} value The value to include in the event detail.
     * 
     * @private
     */
    sendOptionSelect(value) {
        this.dispatchEvent(new CustomEvent('optionselect', { detail: value }));
    }

    /**
     * Puts the menu into its final open state,
     * updating classes and removing its dynamic width.
     * 
     * @private
     */
    setOpen() {
        const { classList, style } = this;

        classList.add('open');

        classList.remove('opening');

        style.width = '';
    }

    /**
     * Sizes and positions the menu at the specified CSS left and top offsets.
     * 
     * Sets the menu's width to the width of the top-level menu.
     * Initially positions the menu at the specified offsets,
     * adjusting vertically if the menu would overflow the bottom of its containing block.
     * 
     * @param {number} left The CSS left offset for the menu.
     * @param {number} top The CSS top offset for the menu.
     * 
     * @private
     */
    size(left, top) {
        const boxShadowWidth = 5;

        const { shadowRoot, style } = this;

        const { offsetWidth } = shadowRoot.getElementById('menu');

        style.left = `${left}px`;
        style.top = `${top}px`;

        style.width = `${offsetWidth + boxShadowWidth}px`;

        const { offsetHeight, offsetParent, offsetTop } = this;

        const overflow = offsetTop + offsetHeight - offsetParent.offsetHeight;

        if (overflow > 0) {
            style.top = `${top - overflow}px`;
        }
    }
}

customElements.define('drilldown-menu', DrilldownMenu);
